import clsx from 'clsx';

import { BuyerIntentLeadClick } from 'components/BuyerIntent';
import Card from 'components/Card';
import CtaButton from 'components/CtaButton';
import FeatureCheck from 'components/FeatureCheck';
import Grid from 'components/Grid';
import PricingComponents from 'components/Pricing';
import Rating from 'components/Rating';
import Typography from 'components/Typography';
import { EventContext, useEventContext } from 'context/EventContext';
import { getPricingUrl } from 'helpers/listingHelpers';
import usePplForm from 'hooks/usePplForm';
import useViewport from 'hooks/useViewport';

import PricingPlansButton from '../../../components/PricingPlansButton';
import PricingTrialButton from '../../../components/PricingTrialButton';
import BorderGradientContainer from './components/BorderGradientContainer';

import classes from './Pricing.module.scss';

const PricingOverview = ({
  listing,
  showValueForMoney,
  showPplPricingBanner,
}) => {
  const { up } = useViewport();

  const hasFreeTrial = listing.is_premium && !!listing.cta_urls.trial;
  const isChecked = (slug) => listing.pricing.categories.includes(slug);
  const pricingUrl = getPricingUrl(listing);

  const showPplButton = showPplPricingBanner && !pricingUrl;

  const { addEvent } = useEventContext();
  const { buyerIntentProductId, buyerIntentCategoryId, handleClick } =
    usePplForm({
      productId: listing.global_product_id,
      sourceLocation: 'listing-detail/pricing-overview:get-price',
      formTemplate: 'price',
      includeProductInConfig: true,
      enabled: showPplButton,
    });

  return (
    <EventContext
      value={{
        engagementComponent: 'product_pricing',
        name: 'engagement_product_pricing',
      }}
    >
      {showValueForMoney && (
        <div className={classes.valueForMoneyContainer}>
          <Typography
            variant="heading-medium"
            className={classes.valueForMoney}
          >
            Value for money rating:
          </Typography>
          <Rating.Link
            average={listing.reviews.by_field.rating_value.toString()}
            totals={listing.num_reviews}
            isCompact={true}
            size="small"
          />
        </div>
      )}
      <Card
        className={clsx(classes.card, {
          [classes.cardVariant]: showPplButton,
        })}
      >
        <Grid container={true} spacing={up('lg') ? 6 : 4}>
          <Grid item={true} xs={12} sm={6} lg={4}>
            <BorderGradientContainer
              className={classes.borderGradientContainer}
            >
              <PricingComponents.DisplayLabel
                pricing={listing.pricing}
                labelVariant={up('sm') ? 'small' : 'large'}
                variant={up('sm') ? 'large' : 'small'}
              />
              <PricingComponents.Display
                pricing={listing.pricing}
                variant={up('sm') ? 'large' : 'small'}
              />
              {showPplButton && (
                <div className={classes.pplButton}>
                  <BuyerIntentLeadClick
                    productId={buyerIntentProductId}
                    categoryId={buyerIntentCategoryId}
                    sourceLocation="listing-detail/pricing-overview:get-price"
                  >
                    <CtaButton
                      fullWidth={true}
                      onClick={handleClick}
                      {...addEvent({
                        engagementComponent: 'ppl_banner',
                        engagementDestination: 'go-to_ppl-price-form',
                        engagementDetails: 'pricing-overview_get-price',
                        name: 'ppl_cta',
                      })}
                    >
                      Get Price
                    </CtaButton>
                  </BuyerIntentLeadClick>
                </div>
              )}
            </BorderGradientContainer>
          </Grid>
          <Grid item={true} xs={12} sm={6} lg={4}>
            <span className={classes.pricingDetailsTitle}>Pricing details</span>
            <div className={classes.itemsContainer}>
              <FeatureCheck checked={isChecked('subscription')}>
                Subscription
              </FeatureCheck>
              <FeatureCheck checked={isChecked('free-trial')}>
                Free trial
              </FeatureCheck>
              <FeatureCheck checked={isChecked('free')}>Free plan</FeatureCheck>
              <div className={classes.pricingRangeContainer}>
                <span className={classes.pricingRangeName}>Pricing range</span>
                <PricingComponents.Rating
                  rating={listing.pricing.price_range}
                />
              </div>
            </div>
          </Grid>
          <Grid
            item={true}
            xs={12}
            sm={6}
            lg={4}
            className={classes.buttonsColumn}
          >
            <div className={classes.buttonsContainer}>
              <PricingPlansButton
                pricingUrl={pricingUrl}
                globalProductId={listing.global_product_id}
                fullWidth={true}
              />
              {hasFreeTrial && (
                <PricingTrialButton
                  trialUrl={listing.cta_urls.trial}
                  globalProductId={listing.global_product_id}
                />
              )}
            </div>
          </Grid>
        </Grid>
      </Card>
    </EventContext>
  );
};

export default PricingOverview;
